import type {
  Bake,
  BaseUnit,
  ContentBundle,
  Customer,
  GameState,
  IngredientId,
  MenuItem,
  Recipe,
  RecipeId,
  AssetManifest,
  ServeLogEntry,
} from '../types';
import { OVEN_RACKS, PRE_OPEN_GAME_SECONDS, OPEN_GAME_SECONDS } from '../config';
import { events } from './events';
import { loadSave, writeSave } from './save';
import { computeMixAccuracy } from '../systems/MixingSystem';
import { customersSystem } from '../systems/CustomerSystem';

export interface StoreShape {
  content: ContentBundle;
  manifest: AssetManifest;
  state: GameState;
}

function defaultGameState(): GameState {
  return {
    version: 2,
    bakerCostume: 0,
    bakeryName: "Bakery Pro",
    coins: 0,
    day: 1,
    inventory: {},
    ownedEquipment: [],
    ownedDecorations: [],
    placedDecorations: [],
    unlockedRecipes: [],
    customRecipes: [],
    todaysMenu: [],
    mixing: null,
    carriedBake: null,
    activeBakes: [],
    clockGameSec: 0,
    phase: 'pre-open',
    dayQueueIds: [],
    todaysServes: [],
    customers: [],
    score: 0,
  };
}

let idCounter = 0;
function freshId(prefix: string): string {
  idCounter += 1;
  return `${prefix}-${Date.now().toString(36)}-${idCounter}`;
}

class Store {
  private _content: ContentBundle | null = null;
  private _manifest: AssetManifest | null = null;
  private _state: GameState = defaultGameState();

  init(content: ContentBundle, manifest: AssetManifest): void {
    this._content = content;
    this._manifest = manifest;
    const saved = loadSave();
    if (saved) {
      this._state = saved;
      // Fields added after v2 shipped — default-fill for older v2 saves.
      this._state.carriedBake ??= null;
      this._state.clockGameSec ??= 0;
      this._state.phase ??= 'pre-open';
      this._state.dayQueueIds ??= [];
      this._state.todaysServes ??= [];
      this._state.customers ??= [];
    } else {
      // Fresh game: unlock all seeded recipes for Phase 1.
      this._state.unlockedRecipes = content.recipes.map((r) => r.id);
      // Stock the pantry with a starter amount of every ingredient so Phase 1
      // doesn't gate on the not-yet-built ingredient shop.
      for (const ing of content.ingredients) this._state.inventory[ing.id] = 999;
    }
    // The persistent roster: hydrate from content once, then visits and
    // satisfaction accrue in the save (kids' edits to customers.json apply
    // to NEW saves; existing saves keep their history).
    if (this._state.customers.length === 0) {
      this._state.customers = content.customers.map((c) => ({ ...c, prefs: { ...c.prefs } }));
    }
    events.emit('state:loaded', undefined);
  }

  get content(): ContentBundle {
    if (!this._content) throw new Error('Store not initialized');
    return this._content;
  }

  get manifest(): AssetManifest {
    if (!this._manifest) throw new Error('Store not initialized');
    return this._manifest;
  }

  get state(): GameState {
    return this._state;
  }

  recipeById(id: RecipeId): Recipe | undefined {
    return (
      this._content?.recipes.find((r) => r.id === id) ??
      this._state.customRecipes.find((r) => r.id === id)
    );
  }

  batchById(id: string): MenuItem | undefined {
    return this._state.todaysMenu.find((m) => m.id === id);
  }

  customerById(id: string): Customer | undefined {
    return this._state.customers.find((c) => c.id === id);
  }

  get activeCustomer(): Customer | undefined {
    const id = this._state.dayQueueIds[0];
    return id ? this.customerById(id) : undefined;
  }

  setBaker(costume: number): void {
    this._state.bakerCostume = costume;
    events.emit('baker:changed', { costume });
    this.persist();
  }

  // --- Day clock -------------------------------------------------------------

  // Advance the game clock (game-seconds). Phase transitions fire exactly once
  // as the clock crosses each boundary. Ticks stop once the day is closed.
  tickClock(dtGameSec: number): void {
    if (this._state.phase === 'closed') return;
    this._state.clockGameSec += dtGameSec;
    events.emit('clock:tick', { gameSec: this._state.clockGameSec });

    if (this._state.phase === 'pre-open' && this._state.clockGameSec >= PRE_OPEN_GAME_SECONDS) {
      this.openShop();
    }
    if (this._state.phase === 'open' &&
        this._state.clockGameSec >= PRE_OPEN_GAME_SECONDS + OPEN_GAME_SECONDS) {
      this.closeShop();
    }
    // Clock changes every frame; persisting here would hammer localStorage.
    // The clock is saved piggyback on every other mutation's persist().
  }

  // Player chose to open early: jump the clock to opening time.
  openShopNow(): void {
    if (this._state.phase !== 'pre-open') return;
    this._state.clockGameSec = PRE_OPEN_GAME_SECONDS;
    this.openShop();
    events.emit('clock:tick', { gameSec: this._state.clockGameSec });
  }

  openShop(): void {
    if (this._state.phase !== 'pre-open') return;
    this._state.phase = 'open';
    this._state.clockGameSec = Math.max(this._state.clockGameSec, PRE_OPEN_GAME_SECONDS);
    if (this._state.dayQueueIds.length === 0 && this._state.todaysServes.length === 0) {
      this._state.dayQueueIds = customersSystem.seedDayQueue(this._state.customers, this._state.day);
    }
    events.emit('phase:open', undefined);
    this.persist();
  }

  closeShop(): void {
    if (this._state.phase === 'closed') return;
    this._state.phase = 'closed';
    events.emit('phase:close', undefined);
    this.persist();
  }

  // --- Serving ---------------------------------------------------------------

  setMenuPrice(batchId: string, price: number): void {
    const batch = this.batchById(batchId);
    if (!batch) return;
    batch.price = Math.max(1, Math.min(20, Math.round(price)));
    events.emit('menu:price', { batchId, price: batch.price });
    this.persist();
  }

  // Serve one unit of the given batch to the customer at the front of the
  // queue. Returns the ledger entry, or null when there's no customer or the
  // batch isn't sellable.
  serveCustomer(batchId: string): ServeLogEntry | null {
    const customer = this.activeCustomer;
    const batch = this.batchById(batchId);
    if (!customer || !batch) return null;
    if (batch.status !== 'ready' || batch.quantity <= 0) return null;
    const recipe = this.recipeById(batch.recipe);
    if (!recipe) return null;

    const satisfaction = customersSystem.scoreServe(batch, recipe, customer);
    customer.visits += 1;
    customer.cumulativeSatisfaction += satisfaction;

    batch.quantity -= 1;
    this._state.coins += batch.price;
    this._state.score += Math.max(0, satisfaction);

    const entry: ServeLogEntry = {
      customerId: customer.id,
      batchId: batch.id,
      recipe: batch.recipe,
      satisfaction,
      coinsEarned: batch.price,
      served: true,
    };
    this._state.todaysServes.push(entry);
    this._state.dayQueueIds.shift();

    events.emit('coins:changed', { coins: this._state.coins });
    events.emit('serve:resolved', { entry });
    events.emit('queue:advance', { customerId: this._state.dayQueueIds[0] ?? null });
    events.emit('menu:changed', undefined);
    this.persist();
    return entry;
  }

  skipCustomer(): ServeLogEntry | null {
    const customer = this.activeCustomer;
    if (!customer) return null;
    const entry: ServeLogEntry = {
      customerId: customer.id,
      batchId: null,
      recipe: null,
      satisfaction: 0,
      coinsEarned: 0,
      served: false,
    };
    this._state.todaysServes.push(entry);
    this._state.dayQueueIds.shift();
    events.emit('serve:resolved', { entry });
    events.emit('queue:advance', { customerId: this._state.dayQueueIds[0] ?? null });
    this.persist();
    return entry;
  }

  // --- Kitchen / mixing ----------------------------------------------------

  // Start a new batch of the given recipe. Abandons any batch still mixing
  // (one bowl). The batch appears on today's menu immediately.
  chooseRecipe(recipeId: RecipeId): MenuItem {
    if (this._state.mixing) {
      this._state.todaysMenu = this._state.todaysMenu.filter(
        (m) => m.id !== this._state.mixing!.batchId,
      );
    }
    const batch: MenuItem = {
      id: freshId('batch'),
      recipe: recipeId,
      status: 'mixing',
      mixAccuracy: 0,
      quantity: 0,
      price: 5,
      toppings: [],
    };
    this._state.todaysMenu.push(batch);
    this._state.mixing = { batchId: batch.id, recipe: recipeId, adds: [] };
    events.emit('menu:changed', undefined);
    events.emit('mix:changed', undefined);
    this.persist();
    return batch;
  }

  addToBowl(ingredient: IngredientId, qty: number, unit: BaseUnit): boolean {
    if (!this._state.mixing || qty <= 0) return false;
    this._state.mixing.adds.push({ ingredient, qty, unit });
    events.emit('mix:changed', undefined);
    this.persist();
    return true;
  }

  emptyBowl(): void {
    if (!this._state.mixing) return;
    this._state.mixing.adds = [];
    events.emit('mix:changed', undefined);
    this.persist();
  }

  freeRack(): 1 | 2 | null {
    const used = new Set(this._state.activeBakes.map((b) => b.rack));
    for (let r = 1; r <= OVEN_RACKS; r++) {
      if (!used.has(r as 1 | 2)) return r as 1 | 2;
    }
    return null;
  }

  // Lock the batch: compute mix accuracy, pour it into a pan and carry it to
  // the oven. Fails (returns null) when nothing is mixing, the bowl is empty,
  // or the baker already has a pan in hand. Rack choice happens at the oven —
  // the pan only starts cooking when placed (placePan).
  bakeIt(): Bake | null {
    const mixing = this._state.mixing;
    if (!mixing || mixing.adds.length === 0) return null;
    if (this._state.carriedBake) return null;
    const recipe = this.recipeById(mixing.recipe);
    const batch = this.batchById(mixing.batchId);
    if (!recipe || !batch) return null;

    batch.mixAccuracy = computeMixAccuracy(mixing, recipe).accuracy;
    batch.status = 'baking';

    const bake: Bake = {
      id: freshId('bake'),
      batchId: batch.id,
      recipe: recipe.id,
      rack: null,
      startedAt: 0,
      // Game-seconds: recipe minutes are game-minutes, ticked at
      // TIME_RATE_MULTIPLIER× real time alongside the day clock.
      durationSec: recipe.minutes * 60,
      ovenTempAtStart: 0,
      doneness: 'raw',
    };
    this._state.carriedBake = bake;
    this._state.mixing = null;
    events.emit('menu:changed', undefined);
    events.emit('mix:changed', undefined);
    this.persist();
    return bake;
  }

  // Set the carried pan onto a free rack. Cooking starts here (caller wires
  // the BakingSystem runtime). Returns the placed bake or null when the rack
  // is taken / nothing is carried.
  placePan(rack: 1 | 2): Bake | null {
    const bake = this._state.carriedBake;
    if (!bake) return null;
    if (this._state.activeBakes.some((b) => b.rack === rack)) return null;
    bake.rack = rack;
    this._state.activeBakes.push(bake);
    this._state.carriedBake = null;
    events.emit('bake:started', { bake });
    this.persist();
    return bake;
  }

  // --- Oven ----------------------------------------------------------------

  updateBakeDoneness(bakeId: string, doneness: Bake['doneness']): void {
    const bake = this._state.activeBakes.find((b) => b.id === bakeId);
    if (!bake) return;
    if (bake.doneness === doneness) return;
    bake.doneness = doneness;
    events.emit('bake:doneness', { bakeId, doneness });
    this.persist();
  }

  // Pull the pan out: lock doneness onto the batch, free the rack, batch is
  // ready to sell with the recipe's full yield. Caller releases the
  // BakingSystem runtime (baking.forget) — store stays system-agnostic.
  takeOut(bakeId: string): MenuItem | null {
    const bake = this._state.activeBakes.find((b) => b.id === bakeId);
    if (!bake) return null;
    const batch = this.batchById(bake.batchId);
    const recipe = this.recipeById(bake.recipe);
    if (!batch || !recipe) return null;

    batch.status = 'ready';
    batch.doneness = bake.doneness;
    batch.quantity = recipe.yield;
    this._state.activeBakes = this._state.activeBakes.filter((b) => b.id !== bakeId);
    events.emit('bake:finished', { bake });
    events.emit('menu:changed', undefined);
    this.persist();
    return batch;
  }

  // --- Day -----------------------------------------------------------------

  finishDay(): void {
    this._state.day += 1;
    this._state.todaysMenu = [];
    this._state.activeBakes = [];
    this._state.carriedBake = null;
    this._state.mixing = null;
    this._state.clockGameSec = 0;
    this._state.phase = 'pre-open';
    this._state.dayQueueIds = [];
    this._state.todaysServes = [];
    events.emit('day:advance', { day: this._state.day });
    this.persist();
  }

  persist(): void {
    writeSave(this._state);
    events.emit('state:saved', undefined);
  }
}

export const store = new Store();
